import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { BarChart3, Info } from 'lucide-react';

/**
 * What people ask about, by stored query_category.
 *
 * A classification the writer already records — not something inferred here —
 * so this carries no accuracy caveat, unlike the failure heuristic on the same
 * tab. It does state its own coverage: roughly 15% of turns have no category,
 * and saying so stops the percentages reading as exhaustive.
 *
 * `(uncategorised)` renders in grey and always last regardless of size, so a
 * large unknown bucket cannot masquerade as the leading category.
 *
 * When `onSelect` is provided each row is a click-through: the raw stored
 * value (or the `(uncategorised)` sentinel) is handed back so the caller can
 * open Sessions filtered to conversations that touched the topic. The rows
 * count TURNS; the drill-through lists whole conversations containing at
 * least one such turn — related populations, not the same number.
 */

const label = (c) =>
  c === '(uncategorised)' ? 'Not categorised' : c.replace(/_/g, ' ');

function CategoryRow({ row, max, muted, onSelect }) {
  const body = (
    <>
      <span
        className={`w-[150px] truncate ${muted
          ? 'text-gray-500'
          : 'capitalize text-gray-600 dark:text-gray-300'}`}
        title={row.category}
      >
        {label(row.category)}
      </span>
      <div className="flex-1 h-2 rounded bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
        <div
          className={`h-full rounded ${muted ? 'bg-zinc-400' : 'bg-[#378ADD]'}`}
          style={{ width: `${max ? Math.round(100 * row.turns / max) : 0}%` }}
        />
      </div>
      <span className="w-16 text-right tabular-nums text-gray-500">
        {row.turns.toLocaleString()}
      </span>
      <span className="w-12 text-right tabular-nums text-gray-400">
        {row.pct === null || row.pct === undefined ? '—' : `${row.pct}%`}
      </span>
    </>
  );

  const rowClass = `flex items-center gap-3 py-1.5 text-sm ${muted
    ? 'opacity-60 border-t border-zinc-200 dark:border-zinc-800 mt-1 pt-2'
    : ''}`;

  if (!onSelect) return <div className={rowClass}>{body}</div>;
  return (
    <button
      type="button"
      onClick={() => onSelect(row.category)}
      className={`${rowClass} w-full text-left rounded transition-colors
        hover:bg-zinc-50 dark:hover:bg-zinc-900 cursor-pointer`}
      title={`See conversations about “${label(row.category)}”`}
    >
      {body}
    </button>
  );
}

export default function QueryCategoriesCard({ data, loading, onSelect }) {
  const rows = data?.categories || [];
  const named = rows.filter((r) => r.category !== '(uncategorised)');
  const unknown = rows.find((r) => r.category === '(uncategorised)');
  const max = named.length ? Math.max(...named.map((r) => r.turns)) : 0;

  return (
    <Card className="min-w-0">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          What people ask about
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-sm text-gray-500 py-6">Loading…</div>}
        {!loading && named.length === 0 && (
          <div className="text-sm text-gray-500 py-6">No categorised questions in this range.</div>
        )}

        {!loading && named.map((r) => (
          <CategoryRow key={r.category} row={r} max={max} onSelect={onSelect} />
        ))}

        {/* Always last, always grey — a big unknown bucket must not read as the
            leading category just because it happens to be the largest. */}
        {!loading && unknown && (
          <CategoryRow row={unknown} max={max} muted onSelect={onSelect} />
        )}

        {!loading && data?.categorised_pct !== null && data?.categorised_pct !== undefined && (
          <p className="pt-3 text-xs text-gray-500 flex gap-2">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              <strong>{data.categorised_pct}%</strong> of questions carry a category. This is a
              label the system already records for each question, not a guess made here — but the
              percentages describe the categorised portion, not all traffic.
              {onSelect ? ' Click a category to read the conversations behind it.' : ''}
            </span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
